
import { Transaction, CreditCard } from '@/types';
import { calculateBillDueDate } from '@/utils/cardBillUtils';
import { logger } from '@/utils/logger';

export type CardExpenseDisplayMode = 'due_date' | 'transaction_date';

/**
 * Processa todas as transações aplicando a data efetiva correta
 * 
 * REGRA CONFIGURÁVEL:
 * - Receitas: sempre data original
 * - Despesas sem cartão: sempre data original  
 * - Despesas de cartão:
 *   - Se cardExpenseDisplayMode = 'due_date': data de vencimento da fatura (padrão)
 *   - Se cardExpenseDisplayMode = 'transaction_date': data original da transação
 * 
 * @param includeFutures - Se false, exclui transações pendentes/parciais
 * @param cardExpenseDisplayMode - Modo de exibição de despesas de cartão (padrão: 'due_date')
 */
export const processAllTransactionsForReports = (
  transactions: Transaction[],
  cards: CreditCard[],
  includeFutures: boolean = true,
  cardExpenseDisplayMode: CardExpenseDisplayMode = 'due_date'
): Transaction[] => {
  logger.debug('[CardProcessor] Processing transactions for reports', {
    total: transactions.length,
    cardsAvailable: cards.length,
    includeFutures,
    cardExpenseDisplayMode
  });

  const result = transactions.map(transaction => {
    // Se não incluir futuros e a transação está pendente/parcial, marcar para exclusão
    if (!includeFutures && (transaction.status === 'pending' || transaction.status === 'partial')) {
      return {
        ...transaction,
        shouldExcludeFromReports: true,
        displayDate: transaction.date
      };
    }

    // Para transações de cartão (TODAS, não apenas pendentes)
    // IMPORTANTE: Não processar pagamentos de fatura como compras do cartão
    if (transaction.cardId && transaction.type === 'despesa' && !transaction.isBillPayment) {
      const card = cards.find(c => c.id === transaction.cardId);
      if (!card || !card.dueDate) {
        return transaction;
      }

      // NOVA LÓGICA: Verificar preferência do usuário
      if (cardExpenseDisplayMode === 'transaction_date') {
        // Usar data original da transação (data da compra)
        return {
          ...transaction,
          displayDate: transaction.date,
          isCardTransaction: true
        };
      }

      // Comportamento padrão: usar data de vencimento da fatura
      const billDueDate = calculateBillDueDate(transaction.date, card.dueDate, card.closingDate);
      
      return {
        ...transaction,
        billDueDate,
        displayDate: billDueDate,
        isCardTransaction: true
      };
    }

    // Para transações não-cartão com status pending ou partial
    if (transaction.status === 'pending' || transaction.status === 'partial') {
      return {
        ...transaction,
        displayDate: transaction.date,
        isPendingNonCardTransaction: true
      };
    }

    // Para transações normais pagas (não-cartão)
    return {
      ...transaction,
      displayDate: transaction.date
    };
  });

  const cardTransactionsProcessed = result.filter(t => (t as any).isCardTransaction).length;
  logger.debug('[CardProcessor] Processing complete', {
    processed: result.length,
    cardTransactions: cardTransactionsProcessed
  });

  return result;
};

/**
 * LEGACY: Mantido para compatibilidade, mas agora chama processAllTransactionsForReports
 */
export const processCardTransactionsForReports = (
  transactions: Transaction[],
  cards: CreditCard[],
  includeFutures: boolean = true
): Transaction[] => {
  return processAllTransactionsForReports(transactions, cards, includeFutures);
};

/**
 * Determina se uma transação de cartão deve aparecer com sua categoria original
 * ou como "Fatura de Cartão" no gráfico de pizza
 */
export const shouldUseOriginalCategory = (transaction: Transaction): boolean => {
  // Se não é transação de cartão, usar categoria original
  if (!transaction.cardId || transaction.type !== 'despesa') {
    return true;
  }

  // Se foi totalmente paga, usar categoria original
  if (transaction.status === 'paid' && transaction.paidPortion && 
      transaction.paidPortion >= Math.abs(transaction.originalAmount || transaction.amount)) {
    return true;
  }

  // Se está pendente ou parcialmente paga, usar categoria de fatura
  return false;
};
